import { useQueryClient } from "@tanstack/react-query";

import API from "../../../";
import { useMutationWrapper } from "../../wrappers";
import { roomDetailsKey, updateRoomKey } from "../room-query-keys";
import { UpdateRoomApiDto } from "../../../rooms/dtos/update-room.api.dto";
import { UPDATE_ROOM_ERP_INTEGRATIONS_ERROR } from "../../../rooms/errors";

export interface UpdateRoomManagerMutationParams {
  roomId: string;
  onSuccess?: () => void;
  onError?: (err: Error) => void;
}

export function useMutationUpdateRoomManager({
  roomId,
  onError,
  onSuccess,
}: UpdateRoomManagerMutationParams) {
  const queryClient = useQueryClient();

  const invalidateQueryKeys = () => {
    queryClient.invalidateQueries({ queryKey: roomDetailsKey(roomId) });
  };

  const handleSuccess = (
    _data: boolean,
    _variables: Pick<UpdateRoomApiDto, "managerId" | "_id">
  ) => {
    invalidateQueryKeys();
    onSuccess?.();
  };

  const handleError = (err: Error & { code: string }) => {
    invalidateQueryKeys();

    if (err.code === UPDATE_ROOM_ERP_INTEGRATIONS_ERROR) {
      onSuccess?.();
      return;
    }
    onError?.(err);
  };

  const { mutate, isPending, isSuccess } = useMutationWrapper<
    boolean,
    Pick<UpdateRoomApiDto, "managerId" | "_id">
  >({
    mutationKey: updateRoomKey(roomId),
    mutationFn: API.Rooms.updateRoom,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  return {
    mutate,
    isPending,
    isSuccess,
  };
}
